import axios, { AxiosError } from 'axios';

const AUTH_BASE_URL = '/api/auth/auth/kc/users';

export interface LoginRequest {
  username: string;
  password: string;
}

export interface LoginUser {
  username: string;
  displayName: string;
  roles: string[];
}

export interface LoginResponse {
  token: string;
  user: LoginUser;
  expiresAt: number;
}

interface AuthApiResponse<T> {
  success: boolean;
  code: number;
  message?: string;
  msg?: string;
  data?: T;
}

interface KeycloakUserInfo {
  preferred_username?: string;
  display_name?: string;
  name?: string;
  family_name?: string;
  given_name?: string;
  realm_access?: {
    roles?: unknown;
  };
}

interface KeycloakLoginData {
  access_token?: string;
  expires_in?: number;
  token_type?: string;
  userInfo?: KeycloakUserInfo;
}

function getErrorMessage(error: unknown, fallbackMessage: string): string {
  if (axios.isAxiosError(error)) {
    const responseError = error as AxiosError<AuthApiResponse<unknown>>;
    return (
      responseError.response?.data?.message || responseError.response?.data?.msg || fallbackMessage
    );
  }
  return error instanceof Error ? error.message : fallbackMessage;
}

function unwrapResponse<T>(response: AuthApiResponse<T>, fallbackMessage: string): T {
  if (!response.success || !response.data) {
    throw new Error(response.message || response.msg || fallbackMessage);
  }
  return response.data;
}

function mapUser(userInfo?: KeycloakUserInfo): LoginUser {
  const username = userInfo?.preferred_username?.trim() || '';
  const fullName = `${userInfo?.family_name || ''}${userInfo?.given_name || ''}`.trim();
  const displayName =
    userInfo?.display_name?.trim() || userInfo?.name?.trim() || fullName || username;
  const rawRoles = userInfo?.realm_access?.roles;
  const roles = Array.isArray(rawRoles)
    ? rawRoles.filter((role): role is string => typeof role === 'string')
    : [];

  if (!username) {
    throw new Error('登录响应缺少用户工号');
  }

  return { username, displayName, roles };
}

async function getUserInfo(token: string): Promise<KeycloakUserInfo> {
  try {
    const response = await axios.get<AuthApiResponse<KeycloakUserInfo>>(
      `${AUTH_BASE_URL}/userinfo`,
      {
        headers: { Authorization: token },
      },
    );
    return unwrapResponse(response.data, '登录权限校验失败');
  } catch (error) {
    throw new Error(getErrorMessage(error, '登录权限校验失败'));
  }
}

export async function login(request: LoginRequest): Promise<LoginResponse> {
  let data: KeycloakLoginData;

  try {
    const response = await axios.post<AuthApiResponse<KeycloakLoginData>>(
      `${AUTH_BASE_URL}/login-response-with-cookies`,
      {
        username: request.username.trim(),
        password: request.password,
        responseCookiesPath: '/',
      },
    );
    data = unwrapResponse(response.data, '登录失败');
  } catch (error) {
    throw new Error(getErrorMessage(error, '登录失败'));
  }

  const accessToken = data.access_token?.trim();
  const expiresIn = Number(data.expires_in || 0);

  if (!accessToken || !Number.isFinite(expiresIn) || expiresIn <= 0) {
    throw new Error('登录响应中的令牌无效');
  }

  const token = `Bearer ${accessToken}`;
  const userInfo = await getUserInfo(token);

  return {
    token,
    user: mapUser({ ...data.userInfo, ...userInfo }),
    expiresAt: Date.now() + expiresIn * 1000,
  };
}

export async function validateToken(token: string): Promise<LoginUser> {
  try {
    const response = await axios.get<AuthApiResponse<KeycloakUserInfo>>(
      `${AUTH_BASE_URL}/validate`,
      {
        headers: { Authorization: token },
      },
    );
    const validatedUser = unwrapResponse(response.data, '登录状态已失效');
    const userInfo = await getUserInfo(token);
    return mapUser({ ...validatedUser, ...userInfo });
  } catch (error) {
    throw new Error(getErrorMessage(error, '登录状态已失效'));
  }
}

export async function logout(token: string): Promise<void> {
  await axios.post(
    `${AUTH_BASE_URL}/logout`,
    {},
    {
      headers: { Authorization: token },
    },
  );
}
